// packages
import { format } from 'd3-format';
import { scaleBand, scaleLinear } from 'd3-scale';
import { select } from 'd3-selection';

// local
import { createBase } from '../../utils/d3-base';
import { capfirst, intcomma } from 'journalize';
import { __ } from '../../utils/i18n';

export function boot(data, missing, id = 'deaths-by-race-chart') {
  const container = select(`#${id}`);
  const table = document.getElementById('demographics-race-table');
  const footnote = document.getElementById('demographics-race-footnote');

  data = data
    .filter((d) => d.race != 'total')
    .filter((d) => d.race != 'other')
    .filter((d) => d.race != 'cdph-other');
  const yDomain = ['latino', 'white', 'asian', 'black'].reverse();

  // Buttons
  const ele = document.querySelector('.graphic-section--demographics-by-race');
  const buttons = ele.querySelectorAll('button.toggle');
  const selector = document.getElementById('demographics-age-select');
  const getSelectedVariable = () => {
    const activeButton = ele.querySelector('button.active');
    return activeButton.getAttribute('data-variable');
  };
  const getSelectedAge = () => {
    return selector.value;
  };
  buttons.forEach((b) => {
    b.addEventListener('click', function () {
      if (this.classList.contains('active')) {
      } else {
        const activeButton = ele.querySelector('button.active');
        activeButton.classList.remove('active');
        activeButton.classList.add('inactive');
        this.classList.remove('inactive');
        this.classList.add('active');
        const variable = this.getAttribute('data-variable');
        const hed = ele.querySelector('.chart-head');
        if (variable == 'cases_pct') {
          hed.innerHTML = __(
            'Percentage of <span class="cum-cases-underline">cases</span> vs. <span class="population-underline">population</span>'
          );
        } else {
          hed.innerHTML = __(
            'Percentage of <span class="deaths-underline">deaths</span> vs. <span class="population-underline">population</span>'
          );
        }
        render();
      }
    });
  });
  selector.addEventListener('change', () => {
    render();
  });

  render();

  function render() {
    // empty the container on render every time
    container.html('');
    table.innerHTML = '';
    footnote.innerHTML = '';

    const age = getSelectedAge();
    const variable = getSelectedVariable();
    const filteredData = data.filter((d) => d.age == age);
    const filteredMissingData = missing.filter((d) => d.age == age);

    const { g, x, y, width, xAxis, yAxis, drawAxis } = createBase({
      container,
      height: 300,
      x: scaleLinear(),
      y: scaleBand(),
      margin: { left: 60, bottom: 25 },
      title: `${variable == 'cases_pct' ? __('Cases') : __('Deaths')} ${__(
        'by race and age'
      )}`,
    });

    // set up our x scale
    y.domain(yDomain).padding(0.1);

    // pre-calc the bandwidth
    const bandwidth = y.bandwidth();

    // set up our y scale
    x.domain([0, 0.75]).nice();

    // tell the y axis what we want
    xAxis.tickSize(-300).ticks(4).tickFormat(format('.0%'));

    yAxis.tickSize(0).tickFormat((t) => __(capfirst(t)));

    // draw the axis lines, then remove their domains
    drawAxis();

    const bars = g
      .selectAll('g.race')
      .data(filteredData)
      .join('g')
      .attr('class', 'race')
      .attr('role', 'graphics-object')
      .attr('data-race', (d) => d.race);

    bars
      .append('rect')
      .attr('fill', '#dadfe1')
      .attr('fill-opacity', '0.8')
      .attr('data-value', (d) => d.population_pct)
      .attr('role', 'graphics-symbol')
      .attr('x', x(0))
      .attr('y', (d) => y(d.race))
      .attr('width', (d) => x(d.population_pct))
      .attr('height', bandwidth);

    bars
      .append('rect')
      .attr('fill', variable == 'cases_pct' ? '#3995b7' : '#829db0')
      .attr('fill-opacity', '0.8')
      .attr('data-value', (d) => d[variable])
      .attr('role', 'graphics-symbol')
      .attr('x', (d) => x(0))
      .attr('y', (d) => y(d.race) + bandwidth * 0.33)
      .attr('width', (d) => x(d[variable]))
      .attr('height', bandwidth * 0.33);

    const footnoteFields = {
      cases_pct: {
        total: 'missing_cases_total',
        pct: 'missing_cases_pct',
        name: 'cases',
      },
      deaths_pct: {
        total: 'missing_deaths_total',
        pct: 'missing_deaths_pct',
        name: 'deaths',
      },
    };
    const selectedFootnote = footnoteFields[variable];

    var locale = document.documentElement.lang;

    if (locale == 'es') {
      footnote.innerHTML = `Nota: Hay ${intcomma(
        filteredMissingData[0][selectedFootnote.total]
      )} ${[
        __(selectedFootnote.name),
      ]} sin una raza identificada dentro de este grupo de edad, ${Math.round(
        filteredMissingData[0][selectedFootnote.pct] * 100
      )}% del total.`;
    } else {
      footnote.innerHTML = `Note: There are ${intcomma(
        filteredMissingData[0][selectedFootnote.total]
      )} ${[
        selectedFootnote.name,
      ]} with an unknown race in this age bracket, ${Math.round(
        filteredMissingData[0][selectedFootnote.pct] * 100
      )}% of the total.`;
    }

    table.innerHTML = createTable(filteredData, age, variable);
  }

  return render;
}

const createTable = (data, age, variable) => {
  const order = { latino: 1, white: 2, asian: 3, black: 4 };
  data.forEach((d) => (d['order'] = order[d.race]));
  data = data.sort((a, b) => a.order - b.order);
  let header_label = variable.split('_')[0];
  let total_var = variable == 'deaths_pct' ? 'deaths_total' : 'cases_total';
  const rows = data.map((obj) => {
    return `<tr>
      <th scope="row" class="text-left">${__(capfirst(obj.race))}</th>
      <td class="text-right">${intcomma(obj[total_var])}</td>
      <td class="text-right">${(obj[variable] * 100).toFixed(1)}%</td>
      <td class="text-right">${(obj.population_pct * 100).toFixed(1)}%</td>
    </tr>`;
  });
  return `<table class="dg-table">
    <thead>
      <tr>
        <th scope="col" class="text-left">${__('Race')}</th>
        <th scope="col" class="text-right">${__(capfirst(header_label))}</th>
        <th scope="col" class="text-right">${__(
          capfirst(header_label) + ' Pct.'
        )}</th>
        <th scope="col" class="text-right">${__('Population Pct.')}</th>
      </tr>
    </thead>
    <tbody>
      ${rows.join('')}
    </tbody>
  </table>`;
};
